import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAppData } from './hooks/useAppData';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import FloatingChat from './components/Chat/FloatingChat';
import ChatPage from './components/Chat/ChatPage';
import SettingsMenu from './components/SettingsMenu';
import LanguageSelector from './components/LanguageSelector';
import LandingPage from './components/LandingPage';
import AdminEditCards from './components/AdminEditCards';
import AdminManagement from './components/AdminManagement';
import AdminCalendarPage from './components/Admin/AdminCalendarPage';
import UserManagement from './components/UserManagement';
import TithiCalculatorPage from './components/TithiCalculatorPage';
import EmbeddedBuilderPage from './components/TreeBuilder/EmbeddedBuilderPage';
import TreeSelectionPage from './components/TreeBuilder/TreeSelectionPage';
import TreeDetailPage from './components/TreeBuilder/TreeDetailPage';
import DeveloperPage from './components/DeveloperPage';
import McpPage from './components/McpPage';
import EventsPage from './components/EventsPage';
import LlmSettingsPage from './components/Settings/LlmSettingsPage';
import InvitationLandingPage from './components/InvitationLandingPage';
import Login from './Login';
import VerifyEmailPage from './VerifyEmailPage';
import PhoneSetupPage from './components/PhoneSetupPage';
import { useUserPermissions } from './hooks/usePermissions';
import { useTithiDateResolver } from './hooks/useTithiDateResolver';

// Tithi Calculator Button Component with visibility control
// eslint-disable-next-line no-unused-vars
function TithiCalculatorButton({ onClick }) {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const fetchVisibility = async () => {
            try {
                const settingsDoc = await getDoc(doc(db, 'siteSettings', 'block2'));
                if (settingsDoc.exists()) {
                    setVisible(settingsDoc.data().visible !== false);
                }
            } catch (error) {
                console.error('Error fetching Tithi Calculator menu visibility:', error);
            }
        };
        fetchVisibility();
    }, []);

    if (!visible) return null;

    return (
        <button
            onClick={onClick}
            className="group flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-purple-600 font-medium transition-all duration-200 rounded-full hover:bg-purple-50"
        >
            <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>Tithi Calculator</span>
        </button>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <LanguageProvider>
                <ChatProvider>
                    <Router>
                        <AppContent />
                    </Router>
                </ChatProvider>
            </LanguageProvider>
        </AuthProvider>
    );
}

function AppContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();
    
    // AUTH STATE (from AuthContext)
    const { user, isAdmin, isLoading, needsEmailVerification, needsPhone, skipPhone, logout } = useAuth();
    // All Firestore data subscriptions managed by useAppData hook
    const { trees, treeCalendarEvents, personalCalendarEvents, sharedTreeCalendarEvents, treeMembers } = useAppData(user, isAdmin);
    const resolveEventDate = useTithiDateResolver();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [devDropdownOpen, setDevDropdownOpen] = useState(false);
    const [mobileDevOpen, setMobileDevOpen] = useState(false);

    const hamburgerButtonRef = useRef(null);
    const mobileMenuPanelRef = useRef(null);
    const devDropdownRef = useRef(null);
    
    // Smart home button state
    // eslint-disable-next-line no-unused-vars
    const [savedScrollPosition, setSavedScrollPosition] = useState(0);

    // Use the new permissions hook
    useUserPermissions(user);

    const isHomePage = location.pathname === '/';

    const getHeaderPageName = (pathname) => {
        if (pathname === '/' || !pathname) return '';
        if (pathname.startsWith('/tithi-calculator')) return 'Tithi Calculator';
        if (pathname.startsWith('/trees')) return 'Tree View';
        if (pathname.startsWith('/tree/')) return 'Tree';
        if (pathname.startsWith('/admin/edit-cards')) return 'Admin';
        if (pathname.startsWith('/admin/management')) return 'Admin';
        if (pathname.startsWith('/admin/tithis')) return 'Admin';
        if (pathname.startsWith('/admin/events')) return 'Admin';
        if (pathname.startsWith('/admin/calendar')) return 'Admin';
        if (pathname.startsWith('/admin/data-management')) return 'Admin';
        if (pathname.startsWith('/user-management')) return 'User Management';
        if (pathname.startsWith('/developer')) return 'Calendar API';
        if (pathname.startsWith('/mcp')) return 'MCP Server';
        if (pathname.startsWith('/events')) return 'Events & Reminders';
        if (pathname.startsWith('/builder')) return 'Builder';
        if (pathname.startsWith('/chat')) return 'Chat';
        if (pathname.startsWith('/settings')) return 'Settings';

        const clean = pathname.replace(/^\//, '').split('/')[0] || '';
        return clean
            ? clean
                .split('-')
                .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                .join(' ')
            : '';
    };

    const headerPageName = getHeaderPageName(location.pathname);

    const handleBrandClick = () => {
        navigate('/', { replace: true });
    };

    const handleBreadcrumbClick = () => {
        navigate(location.pathname, { replace: true });
    };

    // Close menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // Auto-redirect new users to phone setup page (skip if already there or on auth pages)
    const authPages = ['/login', '/verify-email', '/add-phone', '/invite'];
    const isAuthPage = authPages.some(p => location.pathname.startsWith(p));
    useEffect(() => {
        if (!isLoading && user && needsPhone && !skipPhone && !isAuthPage) {
            navigate('/add-phone', { state: { from: location.pathname }, replace: true });
        }
    }, [needsPhone, skipPhone, isLoading, user, isAuthPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close dev dropdown on outside click
    useEffect(() => {
        if (!devDropdownOpen) return undefined;
        const onPointerDown = (e) => {
            if (devDropdownRef.current && !devDropdownRef.current.contains(e.target)) {
                setDevDropdownOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [devDropdownOpen]);

    // Close menu on outside click; also scroll to top
    useEffect(() => {
        if (!mobileMenuOpen) return undefined;

        const onPointerDown = (event) => {
            const target = event.target;
            const menuEl = mobileMenuPanelRef.current;
            const buttonEl = hamburgerButtonRef.current;

            const clickedInsideMenu = menuEl && menuEl.contains(target);
            const clickedHamburgerButton = buttonEl && buttonEl.contains(target);

            if (!clickedInsideMenu && !clickedHamburgerButton) {
                setMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };

        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [mobileMenuOpen]);

    // Trees, calendar events, and tree members are loaded via the useAppData hook above.

    // Track scroll position when navigating away from home page
    useEffect(() => {
        // Listen for scroll changes
        const handleScroll = () => {
            setSavedScrollPosition(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // --- HANDLERS ---
    const handleBackToList = () => {
        navigate('/', { replace: true });
    };

    const handleSignOut = async () => {
        await logout();
        navigate('/', { replace: true });
    };

    const handleAdminEditCards = () => {
        navigate('/admin/edit-cards');
    };

    const handleAdminManagement = () => {
        navigate('/admin/management');
    };

    const handleUserManagement = () => {
        navigate('/user-management');
    };

    const handleDeveloperPage = () => {
        navigate('/developer');
    };

    const handleMcpPage = () => {
        navigate('/mcp');
    };

    const handleLlmSettings = () => {
        navigate('/settings/ai-provider');
    };

    const handleTithiCalculator = () => {
        navigate('/tithi-calculator');
        setMobileMenuOpen(false);
    };

    const handleNepaliCalendarClick = () => {
        if (location.pathname !== '/') {
            navigate('/');
            // Wait for navigation, then scroll
            setTimeout(() => {
                const calendarSection = document.getElementById('nepali-calendar-section');
                if (calendarSection) {
                    calendarSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        } else {
            const calendarSection = document.getElementById('nepali-calendar-section');
            if (calendarSection) {
                calendarSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        setMobileMenuOpen(false);
    };

    const handleTreeViewClick = () => {
        navigate('/trees');
        setMobileMenuOpen(false);
    };

    // --- RENDER ---
    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="text-gray-500 text-lg">Loading...</div>
        </div>;
    }

    // Do not force-navigation to Login; allow landing page to be public.
    // Hard gate: email/password users who haven't verified are redirected to /verify-email.
    if (needsEmailVerification && location.pathname !== '/verify-email' && location.pathname !== '/login') {
        return <Navigate to="/verify-email" replace state={{ from: location.pathname }} />;
    }

    const handleDoubleClickEvent = (event) => {
        sessionStorage.setItem('landingPageScrollPosition', window.scrollY.toString());
        if (event.treeId) {
            navigate(`/tree/${event.treeId}`, { state: { highlightEventId: event.id } });
        }
    };

    const handleEventClick = (event) => {
        sessionStorage.setItem('landingPageScrollPosition', window.scrollY.toString());
        if (event.treeId) {
            navigate(`/tree/${event.treeId}`, { state: { highlightEventId: event.id } });
        }
    };

    const mergedCalendarEventsById = new Map();
    [...treeCalendarEvents, ...personalCalendarEvents, ...sharedTreeCalendarEvents].forEach((event) => {
        if (event?.id) mergedCalendarEventsById.set(event.id, event);
    });

    const treesById = new Map((trees || []).map(t => [t.id, t]));

    // Events for LandingPageEventsSection and EventsPage
    const allEvents = Array.from(mergedCalendarEventsById.values()).map(event => {
        const tree = event.treeId ? treesById.get(event.treeId) : null;
        return {
            id: event.id,
            title: event.title,
            name: event.title,
            date: event.dateKey,
            dateKey: event.dateKey,
            personId: event.memberId || event.personId || '',
            repetition: event.repetition || 'none',
            treeId: event.treeId || null,
            tithi: event.tithi || null,
            nepaliDateForRecurrence: event.nepaliDateForRecurrence || null,
            host: tree?.primaryMemberName || null,
            hostLocation: tree?.location || null,
            hostContact: tree?.contact || null,
        };
    });

    const allEnrichedEvents = allEvents.map(event => {
        if (event.tithi && event.repetition !== 'monthly') {
            const resolvedDate = resolveEventDate(event);
            if (resolvedDate) return { ...event, resolvedTithiDate: resolvedDate };
        }
        return event;
    });
    
    const getCreatedAtMillis = (createdAt) => {
        if (!createdAt) return Number.POSITIVE_INFINITY;
        // Firestore Timestamp
        if (typeof createdAt === 'object' && createdAt.seconds != null) {
            const seconds = Number(createdAt.seconds) || 0;
            const nanos = Number(createdAt.nanoseconds) || 0;
            return seconds * 1000 + Math.floor(nanos / 1e6);
        }
        // ISO string or Date-parsable
        const ms = new Date(createdAt).getTime();
        return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
    };

    const derivedPrimaryMemberByTreeId = new Map();
    (treeMembers || []).forEach((m) => {
        if (!m?.treeId || !m?.name) return;
        const ms = getCreatedAtMillis(m.createdAt);
        const existing = derivedPrimaryMemberByTreeId.get(m.treeId);
        if (!existing || ms < existing.ms) {
            derivedPrimaryMemberByTreeId.set(m.treeId, { name: m.name, ms });
        }
    });

    const treePrimaryMemberNameById = new Map();
    (trees || []).forEach((t) => {
        const fromTree = (t.primaryMemberName || '').trim();
        const fromMembers = (derivedPrimaryMemberByTreeId.get(t.id)?.name || '').trim();
        treePrimaryMemberNameById.set(t.id, fromTree || fromMembers || 'Tree Member');
    });

    const allFamilyMembers = treeMembers.map(member => ({
        id: member.id,
        name: member.name,
        relation: treePrimaryMemberNameById.get(member.treeId) || 'Tree Member',
        treeId: member.treeId,
    }));

    return (
        <div className="min-h-screen bg-gray-50">
            <header
                className={
                    !isHomePage
                        ? 'sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm'
                        : mobileMenuOpen
                            ? 'relative z-50 bg-white border-b border-gray-200 shadow-sm'
                            : 'sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm'
                }
                role="banner"
            >
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-14">
                        {/* Left Section: Hamburger (mobile) + Logo */}
                        <div className="flex items-center gap-3">
                            {/* Hamburger Menu Button - Mobile Only */}
                            {isHomePage && (
                                <button
                                    ref={hamburgerButtonRef}
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-all duration-200"
                                    aria-label="Toggle menu"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {mobileMenuOpen ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        )}
                                    </svg>
                                </button>
                            )}

                            {/* Logo */}
                            <div className="flex-shrink-0 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleBrandClick}
                                    className="brand-logo-btn"
                                    aria-label="Go to home"
                                >
                                    <img
                                        src="/HamroPanchangaLogo.png"
                                        alt="HamroPanchanga logo"
                                        className="brand-logo-img"
                                    />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBrandClick}
                                    className="brand-link"
                                    aria-label="Go to home"
                                >
                                    HamroPanchanga
                                </button>
                                {!isHomePage && headerPageName ? (
                                    <>
                                        <span className="mx-2 text-gray-400">|</span>
                                        <button
                                            type="button"
                                            onClick={handleBreadcrumbClick}
                                            className="breadcrumb-title max-w-[140px] sm:max-w-none truncate"
                                            aria-label={`Refresh ${headerPageName}`}
                                        >
                                            {headerPageName}
                                        </button>
                                    </>
                                ) : null}
                            </div>

                            {/* Desktop Navigation Menu (home only) */}
                            {isHomePage && (
                                <nav className="hidden lg:flex items-center gap-1 ml-6">
                                    <button
                                        onClick={handleNepaliCalendarClick}
                                        className="nav-menu-item"
                                    >
                                        <span className="nav-menu-text">Nepali Calendar</span>
                                    </button>
                                    <button
                                        onClick={handleTithiCalculator}
                                        className="nav-menu-item"
                                    >
                                        <span className="nav-menu-text">Tithi Calculator</span>
                                    </button>
                                    <button
                                        onClick={handleTreeViewClick}
                                        className="nav-menu-item"
                                    >
                                        <span className="nav-menu-text">Tree View</span>
                                    </button>
                                    {/* For Developer dropdown */}
                                    <div className="relative" ref={devDropdownRef}>
                                        <button
                                            onClick={() => setDevDropdownOpen(o => !o)}
                                            className="nav-menu-item"
                                        >
                                            <span className="nav-menu-text" style={{display:'inline-flex', alignItems:'center', gap:'4px'}}>
                                                For Developer
                                                <svg
                                                    className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${devDropdownOpen ? 'rotate-180' : ''}`}
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </span>
                                        </button>
                                        {devDropdownOpen && (
                                            <div className="absolute left-0 mt-0 w-44 bg-white border border-gray-200 shadow-lg z-50 py-1 overflow-hidden">
                                                <button
                                                    onClick={() => { handleDeveloperPage(); setDevDropdownOpen(false); }}
                                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                                                >
                                                    Calendar API
                                                </button>
                                                <button
                                                    onClick={() => { handleMcpPage(); setDevDropdownOpen(false); }}
                                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                                                >
                                                    MCP Integration
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </nav>
                            )}
                        </div>

                        {/* Right Section: Language Selector + Sign In/Settings */}
                        <div className="flex items-center gap-3">
                            <LanguageSelector compact={true} />
                            {user ? (
                                <div className="border-l border-gray-200 pl-3">
                                    <SettingsMenu
                                        user={user}
                                        onSignOut={handleSignOut}
                                        isAdmin={isAdmin}
                                        onAdminEditCards={handleAdminEditCards}
                                        onAdminManagement={handleAdminManagement}
                                        onUserManagement={handleUserManagement}
                                        onDeveloperPage={handleDeveloperPage}
                                        onLlmSettings={handleLlmSettings}
                                    />
                                </div>
                            ) : (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="flex items-center gap-2 text-sm font-medium bg-gray-900 text-white px-4 py-2 rounded hover:bg-gray-800 transition-all"
                                >
                                    {t('auth.signIn')}
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation Menu (dropdown attached to header; does not push content) */}
                {isHomePage && mobileMenuOpen && (
                    <div
                        ref={mobileMenuPanelRef}
                        className="absolute left-0 right-0 top-full z-50 bg-white border-b border-gray-200 shadow-lg lg:hidden"
                    >
                        <nav className="flex flex-col items-start px-4 py-3 gap-2">
                            <button
                                onClick={handleNepaliCalendarClick}
                                className="mobile-nav-item"
                            >
                                Nepali Calendar
                            </button>
                            <button
                                onClick={handleTithiCalculator}
                                className="mobile-nav-item"
                            >
                                Tithi Calculator
                            </button>
                            <button
                                onClick={handleTreeViewClick}
                                className="mobile-nav-item"
                            >
                                Tree View
                            </button>
                            {/* For Developer expandable item */}
                            <button
                                onClick={() => setMobileDevOpen(o => !o)}
                                className="mobile-nav-item flex items-center justify-between w-full"
                            >
                                <span>For Developer</span>
                                <svg
                                    className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${mobileDevOpen ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {mobileDevOpen && (
                                <div className="ml-4 flex flex-col">
                                    <button
                                        onClick={() => { handleDeveloperPage(); setMobileMenuOpen(false); setMobileDevOpen(false); }}
                                        className="mobile-nav-item text-left"
                                    >
                                        Calendar API
                                    </button>
                                    <button
                                        onClick={() => { handleMcpPage(); setMobileMenuOpen(false); setMobileDevOpen(false); }}
                                        className="mobile-nav-item text-left"
                                    >
                                        MCP Integration
                                    </button>
                                </div>
                            )}
                        </nav>
                    </div>
                )}
            </header>

            {/* Soft banner: prompt verified users to add their WhatsApp number */}
            {user && needsPhone && location.pathname !== '/add-phone' && (
                <div className="bg-green-50 border-b border-green-200 px-4 py-2 flex items-center justify-center gap-3 text-sm">
                    <span className="text-green-800">
                        📱 {t('phoneSetup.bannerText')}
                    </span>
                    <button
                        onClick={() => navigate('/add-phone', { state: { from: location.pathname } })}
                        className="text-green-700 font-medium underline hover:text-green-900 whitespace-nowrap"
                    >
                        {t('phoneSetup.bannerAction')}
                    </button>
                </div>
            )}

            <main role="main">
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/add-phone" element={<PhoneSetupPage />} />
                    <Route path="/" element={
                        <LandingPage
                            user={user}
                            isAdmin={isAdmin}
                            trees={trees}
                            treeMembers={treeMembers}
                            events={allEvents}
                            familyMembers={allFamilyMembers}
                            onDoubleClickEvent={handleDoubleClickEvent}
                            onEventClick={handleEventClick}
                            onSignIn={() => navigate('/login')}
                        />
                    } />

                    <Route path="/admin/edit-cards" element={
                        <AdminEditCards
                            user={user}
                            isAdmin={isAdmin}
                            onBack={handleBackToList}
                        />
                    } />

                    <Route path="/admin/management" element={
                        <AdminManagement
                            user={user}
                            isAdmin={isAdmin}
                            onBack={handleBackToList}
                        />
                    } />

                    <Route path="/admin/management/:tab" element={
                        <AdminManagement
                            user={user}
                            isAdmin={isAdmin}
                            onBack={handleBackToList}
                        />
                    } />

                    <Route path="/admin/calendar" element={
                        <AdminCalendarPage
                            user={user}
                            isAdmin={isAdmin}
                            onBack={handleBackToList}
                        />
                    } />

                    <Route path="/user-management" element={
                        <UserManagement
                            currentUser={user}
                            onBack={handleBackToList}
                        />
                    } />

                    <Route path="/tithi-calculator" element={
                        <TithiCalculatorPage
                            onBack={handleBackToList}
                        />
                    } />

                    <Route path="/trees" element={
                        <TreeSelectionPage
                            user={user}
                            isAdmin={isAdmin}
                        />
                    } />

                    <Route path="/tree/:treeId" element={
                        <TreeDetailPage
                            user={user}
                        />
                    } />

                    <Route path="/builder/:customerId" element={
                        <div className="min-h-screen flex items-center justify-center bg-gray-100">
                            <div className="text-gray-600">This route is deprecated. Please use /trees instead.</div>
                        </div>
                    } />

                    {/* New embedded Tree Builder route backed by Firestore trees */}
                    <Route path="/builder" element={
                        <EmbeddedBuilderPage
                            user={user}
                            isAdmin={isAdmin}
                        />
                    } />

                    <Route path="/developer" element={
                        <DeveloperPage
                            user={user}
                            isAdmin={isAdmin}
                        />
                    } />

                    <Route path="/mcp" element={<McpPage />} />

                    <Route path="/events" element={
                        <EventsPage
                            user={user}
                            events={allEnrichedEvents}
                            familyMembers={allFamilyMembers}
                        />
                    } />

                    <Route path="/chat" element={<ChatPage />} />

                    <Route path="/settings/:tab?" element={<LlmSettingsPage />} />

                    <Route path="/invite/:invitationId" element={<InvitationLandingPage />} />
                </Routes>
            </main>

            <FloatingChat />
        </div>
    );
}